import { Invoice } from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { stockApi } from '$api'
import { StockFile } from '@loadedhub/loaded-stock-api-sdk'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  GetInvoices = 'invoices',
}

/**
 * Get invoices
 * @param start - The start date for filtering invoices
 * @param end - The end date for filtering invoices
 * @param invoiceNumber - The invoice number to filter by (optional)
 * @param supplierName - The supplier name to filter by (optional)
 * @returns an array of invoices
 */
export const getInvoices = async (
  start?: Dayjs,
  end?: Dayjs,
  invoiceNumber?: string,
  supplierName?: string
): Promise<Invoice[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.GetInvoices,
      { start, end, invoiceNumber, supplierName },
    ],
    queryFn: async () => {
      return await stockApi.getInvoices(start, end, invoiceNumber, supplierName)
    },
  })
}

/**
 * Get specific invoice
 * @param id the id of the invoice
 * @returns a specific invoices
 */
export const getInvoice = async (id: string): Promise<Invoice | null> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      return await stockApi.getInvoice(id)
    },
  })
}

/**
 * Get specific initial invoice
 * @param id the id of the invoice
 * @returns a specific invoices
 */
export const getInitialInvoice = async (
  id: string
): Promise<Invoice | null> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['initialInvoice', id],
    queryFn: async () => {
      return await stockApi.getInitialInvoice(id)
    },
  })
}

/**
 * Get unclassified files
 * @param id the id of the invoice
 * @returns a list of unclassified files
 */
export const getUnclassifiedFiles = async (): Promise<StockFile[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return singletonQueryClient.fetchQuery({
    queryKey: ['unclassifiedFiles'],
    queryFn: async () => {
      return await stockApi.getUnclassifiedFiles()
    },
  })
}

/**
 * Add a new invoice
 * @param invoice the invoice to be added
 * @returns the created invoice
 */
export const addInvoice = async (invoice: Invoice): Promise<Invoice> => {
  return await stockApi.createInvoice(invoice)
}

/**
 * put a new invoice
 * @param invoice the invoice to be put
 * @returns the put invoice
 */
export const updateInvoice = async (
  invoice: Invoice
): Promise<Invoice | null> => {
  return stockApi.updateInvoice(invoice.id!, invoice)
}

/**
 * remove a invoice
 * @param invoice the invoice to be deleted
 */
export const removeInvoice = async (invoice: Invoice): Promise<void> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return

  // purge any invoices query results from the local cache as we know this is
  // now going to be invalid
  await singletonQueryClient.invalidateQueries({
    queryKey: [QueryKeys.GetInvoices],
    exact: false,
    refetchType: 'none',
  })

  await stockApi.deleteInvoice(invoice.id!)
}

export interface InvoiceFile {
  data: Blob
  contentType: string
}

export const getInvoiceFile = async (
  invoiceId: string
): Promise<InvoiceFile | null> => {
  const response = await singletonQueryClient.fetchQuery({
    queryKey: ['invoiceFile', invoiceId],
    queryFn: async () => {
      return await stockApi.downloadFile(invoiceId)
    },
  })

  return response.status === 200
    ? {
        data: response.data,
        contentType: response.headers ? response.headers['content-type'] : null,
      }
    : null
}
